import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Types
import { Car } from '../../../../../../../types/SMT';

import VehicleDataModal from './VehicleDataModal';
import { CarInputs } from '../../../../../../../types/ProductFactory';
import { addMonths } from 'date-fns';
import { object, date, number, BaseSchema } from 'yup';

import VehicleDataRegistrationModal from './VehicleDataRegistrationModal';
import VehicleDataOverview from './VehicleDataOverview';
import VehicleDataActions from './VehicleDataActions';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { isObjectEmpty } from '../../../../../../common/src/utils/objectUtils';
import useIsNLplan from '../../../../../../common/src/hooks/useIsNLPlan';
import useFlowQuestions from '../../../../hooks/useFlowQuestions';
import AdvisoryFlowMainContainer from '../../../ui/advisory-flow/components/app-shell/AdvisoryFlowMainContainer';
import QuestionGroupBody from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupBody';
import QuestionGroupAssistantQuestion from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';

interface Props {
  vehicle: Car;
  handleChange: (arg0: Car) => void;
  previousSubView: () => void;
  nextQuestionHandler: () => void;
  handleNewSearch: () => void;
}

const VehicleData: React.FC<React.PropsWithChildren<Props>> = ({
  vehicle,
  handleChange,
  previousSubView,
  nextQuestionHandler,
  handleNewSearch,
}) => {
  if (!vehicle) <>nothing to show</>;
  const { t } = useTranslation();
  const { isNL } = useIsNLplan();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showRegistrationModal, setShowRegistrationModal] =
    useState<boolean>(false);

  const questions = useFlowQuestions();

  const containsCarRegistrationFirstQuestion = questions.find(
    ({ id }) => 'car.registration_first' === id,
  );

  const vehiclePrice =
    vehicle.value != null
      ? vehicle?.value
      : vehicle.price || vehicle.estimated_value;

  //
  // ─── CAR INPUTS NL ───────────────────────────────────────────────
  //

  const carInputsNL: CarInputs[] = [
    {
      key: 'motor_type',
      name: t('Fuel type'),
      type: 'FUEL_SELECT',
      required: true,
    },
    {
      key: 'kw',
      name: `${t('Power')} (kw)`,
      type: 'TEXT',
      tooltip: t('KW-INFO'),
    },
    {
      key: 'weight_in_kg',
      name: `${t('Weight')} (kg)`,
      type: 'TEXT',
    },
    {
      key: 'value',
      name: t('Catalogue value'),
      type: vehiclePrice && vehicle.vat_rate ? 'CURRENCY_INCL_VAT' : 'CURRENCY',
      value:
        vehiclePrice && vehicle.vat_rate
          ? Number(vehiclePrice) * (1 + vehicle.vat_rate)
          : vehiclePrice,
    },
    {
      key: 'registration_first',
      name: t('First registration'),
      type: 'DATE',
    },
  ];

  const schemaNL = object().shape({
    kw: number().typeError(t('This must be a number')).min(0).nullable(),
    price: number().typeError(t('This must be a number')).min(0).nullable(),
    model_year: number().typeError(t('This must be a number')).nullable(),
    registration_first: date()
      .transform(parseDateString)
      .test({
        test: (date) => {
          // Temporary fix because the date input sucks
          if (vehicle.model_year && date && date.getFullYear() > 1970) {
            return !!(date && date.getFullYear() >= vehicle.model_year - 1);
          }
          return true;
        },
        message: t(
          'The year of the registration date should not be earlier than the car model year',
        ),
      })
      .typeError(t('Verify.errors.invalid_date'))
      .max(addMonths(new Date(), 1), t('Date is in the future')),
  });

  //
  // ─── CAR INPUTS BE ─────────────────────────────────────────────────────────
  //

  const carInputsBE: CarInputs[] = [
    {
      key: 'kw',
      name: t('Power'),
      type: 'TEXT',
      required: true,
      tooltip: t('KW-INFO'),
    },
    {
      key: 'cc',
      name: t('Displacement'),
      type: 'TEXT',
      tooltip: t('CC-INFO'),
    },
    {
      key: 'seats',
      name: t('No of seats'),
      type: 'TEXT',
      required: true,
    },
    {
      key: 'co2',
      name: t('Emissions (CO2)'),
      type: 'TEXT',
    },
    {
      key: 'value',
      name: t('Catalogue value'),
      type: 'CURRENCY',
      value: Number(vehiclePrice).toFixed(2),
    },
    {
      key: 'model_year',
      name: t('Model year'),
      type: 'TEXT',
    },
    ...(!containsCarRegistrationFirstQuestion
      ? [
          {
            key: 'registration_first',
            name: t('First registration'),
            type: 'DATE',
            required: true,
          },
        ]
      : []),
  ];

  function parseDateString(value: string, originalValue: string) {
    return new Date(originalValue);
  }

  const schemaBE = object().shape({
    seats: number()
      .required(t('required'))
      .typeError(t('This must be a number'))
      .min(0)
      .nullable(),
    co2: number().typeError(t('This must be a number')).min(0).nullable(),
    cc: number().typeError(t('This must be a number')).min(0).nullable(),
    kw: number()
      .typeError(t('This must be a number'))
      .required(t('required'))
      .min(0)
      .nullable(),
    value: number().typeError(t('This must be a number')).min(0).nullable(),
    model_year: number().typeError(t('This must be a number')).nullable(),

    ...(!containsCarRegistrationFirstQuestion && {
      registration_first: date()
        .transform(parseDateString)
        .when('model_year', (modelYear: number, schema: BaseSchema) =>
          schema.test({
            test: (date: Date) => date && date.getFullYear() >= modelYear - 1,
            message: t(
              'The year of the registration date should not be earlier than the car model year',
            ),
          }),
        )
        .required(t('required'))
        .typeError(t('Verify.errors.invalid_date'))
        .max(addMonths(new Date(), 1), t('Date is in the future')),
    }),
  });

  const carInputs = isNL ? carInputsNL : carInputsBE;
  const schema = isNL ? schemaNL : schemaBE;

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    trigger,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: Object.assign(
      {},
      ...carInputs.map(({ key, value }) => ({
        [key]: value || vehicle?.[key],
      })),
    ),
  });

  useEffect(() => {
    trigger();
  }, []);

  const handleModalYes = () => {
    handleSubmit((vals) => {
      if (
        vals.registration_first &&
        typeof vals.registration_first !== 'string' &&
        vals.registration_first.toISOString
      )
        vals.registration_first = (vals.registration_first as Date)
          .toISOString()
          .split('T')[0];
      handleChange({ ...vehicle, ...vals });
      setIsModalOpen(false);
    })();
  };

  const handleOnRegistrationFormSubmit = () => {
    handleSubmit(
      (vals) => {
        handleChange({ ...vehicle, ...vals });
        setShowRegistrationModal(false);
        setIsModalOpen(false);
        nextQuestionHandler();
      },
      (errors) => {
        if (!errors['registration_first']) {
          setIsModalOpen(true);
        }
      },
    )();
  };

  const onGoNext = () => {
    if (!isObjectEmpty(errors)) {
      if (errors?.['registration_first']) {
        return setShowRegistrationModal(true);
      }

      return setIsModalOpen(true);
    }
    nextQuestionHandler();
  };

  return (
    <AdvisoryFlowMainContainer data-test-id="ProdFac_VehicleData">
      <QuestionGroupAssistantQuestion
        question={t('We found your car! is the information correct?')}
        description={t(
          'If incorrect, you can adjust the details about this car',
        )}
        mb="4"
      />

      {/* Registration modal */}
      <VehicleDataRegistrationModal
        values={getValues('registration_first')}
        errors={errors}
        setFieldValue={setValue}
        setIsModalOpen={setShowRegistrationModal}
        showModal={showRegistrationModal}
        handleModalYes={() => {
          handleOnRegistrationFormSubmit();
        }}
      />
      {/* Modal component for adjusting car data */}
      <VehicleDataModal
        values={getValues()}
        errors={errors}
        setFieldValue={setValue}
        setIsModalOpen={setIsModalOpen}
        showModal={isModalOpen}
        carInputs={carInputs}
        handleModalYes={handleModalYes}
        handleNewSearch={handleNewSearch}
      />

      {/* Actual vehicle data component */}
      <QuestionGroupBody>
        <VehicleDataOverview
          onGoNext={onGoNext}
          setIsModalOpen={setIsModalOpen}
          vehicle={vehicle}
          carInputs={carInputs}
        />
      </QuestionGroupBody>
      <VehicleDataActions
        previousSubView={previousSubView}
        setIsModalOpen={setIsModalOpen}
        onGoNext={onGoNext}
      />
    </AdvisoryFlowMainContainer>
  );
};

export default VehicleData;
